import { useEffect, type JSX } from 'react';
import { AppShell, Group, Burger, Anchor, Box } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/auth/auth-context';
import type { AuthRole } from '@/auth/types';
import { LangSwitcher } from '@/components/LangSwitcher';
import { UploadButton } from '@/components/upload/UploadButton';
import { UploadModal } from '@/components/upload/UploadModal';
import { UploadModalProvider } from '@/components/upload/upload-modal-context';
import { AccountMenu } from '@/components/AccountMenu';
import { startUploader, stopUploader } from '@/lib/upload/uploader';

interface NavItem {
  label: string;
  to: string;
  roles: AuthRole[];
  activePrefix?: string;
}

const NAV_ITEMS: NavItem[] = [
  { label: 'Categories', to: '/categories', roles: ['admin'] },
  { label: 'Wallpapers', to: '/wallpapers', roles: ['admin', 'content'] },
  { label: 'Blog', to: '/blog', roles: ['admin', 'content'] },
  { label: 'Reviews', to: '/customer-reviews', roles: ['admin', 'content'] },
  { label: 'Templates', to: '/interior-templates', roles: ['admin', 'content'] },
  { label: 'Orders', to: '/orders', roles: ['admin'] },
  { label: 'Ideas', to: '/ideas', roles: ['admin'] },
  { label: 'Settings', to: '/settings', roles: ['admin'], activePrefix: '/settings' },
];

const UPLOAD_ROLES: AuthRole[] = ['admin', 'content'];

export function AppLayout(): JSX.Element {
  const [opened, { toggle, close }] = useDisclosure(false);
  const { user, session, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const role = user?.role ?? 'customer';
  const visibleItems = NAV_ITEMS.filter((item) => item.roles.includes(role));
  const canUpload = UPLOAD_ROLES.includes(role);

  useEffect(() => {
    if (!canUpload) return;
    const getToken = (): string | null => session?.access_token ?? null;
    startUploader(getToken);
    return () => {
      stopUploader();
    };
  }, [session, canUpload]);

  const handleSignOut = async (): Promise<void> => {
    await signOut();
    navigate('/login', { replace: true });
  };

  const isNavItemActive = (item: NavItem): boolean => {
    const prefix = item.activePrefix ?? item.to;
    return (
      location.pathname === item.to || location.pathname.startsWith(`${prefix}/`) || location.pathname === prefix
    );
  };

  const handleNavClick = (item: NavItem, e: React.MouseEvent): void => {
    const prefix = item.activePrefix ?? item.to;
    if (location.pathname.startsWith(prefix)) {
      e.preventDefault();
    }
  };

  return (
    <UploadModalProvider>
      <AppShell
        header={{ height: 56 }}
        navbar={{ width: 220, breakpoint: 'sm', collapsed: { desktop: true, mobile: !opened } }}
        padding="md"
      >
        <AppShell.Header>
          <Group h="100%" px="md" justify="space-between" wrap="nowrap">
            <Group gap="lg" wrap="nowrap">
              <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />
              <Group gap={4} visibleFrom="sm">
                {visibleItems.map((item) => {
                  const isActive = isNavItemActive(item);
                  return (
                    <Anchor
                      key={item.to}
                      component={NavLink}
                      to={item.to}
                      px="sm"
                      py={6}
                      underline="never"
                      onClick={(e: React.MouseEvent) => handleNavClick(item, e)}
                      style={{
                        borderRadius: 6,
                        backgroundColor: isActive ? '#f6f1ea' : undefined,
                        color: isActive ? '#1f1a14' : '#4a4137',
                        fontWeight: isActive ? 600 : 500,
                        fontSize: 14,
                      }}
                    >
                      {item.label}
                    </Anchor>
                  );
                })}
              </Group>
            </Group>
            <Group gap="sm" wrap="nowrap">
              {canUpload && <UploadButton />}
              <LangSwitcher />
              <AccountMenu
                email={user?.email ?? 'guest'}
                role={role}
                onSignOut={handleSignOut}
              />
            </Group>
          </Group>
        </AppShell.Header>

        <AppShell.Navbar p="md" hiddenFrom="sm">
          {visibleItems.map((item) => (
            <Anchor
              key={item.to}
              component={NavLink}
              to={item.to}
              onClick={close}
              py="xs"
              c="dark"
              underline="never"
            >
              {item.label}
            </Anchor>
          ))}
        </AppShell.Navbar>

        <AppShell.Main style={{ background: '#fbf7f2' }}>
          <Box>
            <Outlet />
          </Box>
        </AppShell.Main>
      </AppShell>
      {canUpload && <UploadModal />}
    </UploadModalProvider>
  );
}
